<template>
	<AppPopup
		ref="popup"
		:title="getString('edit_address_fields')"
		:buttons="buttons"
		max-width="440"
	>
		<p class="mb-10">{{getString('edit_address_fields_caption')}}</p>
		<div class="edit-address-fields-list">
			<AppTextInput
				v-for="field in editableFields"
				:key="field.key"
				:label="field.label"
				:placeholder="field.label"
				:value="fields[field.key]"
				:readonly="field.readonly"
				@changing-text="v => onChangeField(field.key, v)"
			/>
		</div>
	</AppPopup>
</template>

<script>
import AppPopup from "@/shared/components/AppPopup.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";

import {useLanguageStore} from "@/shared/stores";

export default {
	name: "AppAddressFieldsPopup",
	components: {AppPopup, AppTextInput},
	emits: ["confirm-fields"],
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString};
	},
	props: {
		// Current address data emitted by AppGoogleMapsInput (via, civico, zip, comune, provincia, ...).
		addressData: {type: Object, default: null},
	},
	data() {
		return {
			// Local editable snapshot, populated on open() so cancel discards cleanly.
			fields: {},
			// Whether the user actually changed at least one field this session.
			edited: false,
		};
	},
	computed: {
		editableFields() {
			return [
				{key: "via", label: this.getString("street")},
				{key: "civico", label: this.getString("civic")},
				{key: "zip", label: this.getString("zip")},
				{key: "comune", label: this.getString("city")},
				{key: "provincia", label: this.getString("province")},
				{key: "provincia_codice", label: this.getString("province_code")},
				{key: "regione", label: this.getString("region")},
				{key: "country", label: this.getString("country_field"), readonly: true},
			];
		},
		// Same required set enforced by AppGoogleMapsInput.hasNecessaryFields
		hasRequiredFields() {
			return ["via", "civico", "zip", "provincia"].every(key => !!(this.fields[key] && this.fields[key].trim()));
		},
		buttons() {
			return [
				{text: this.getString("cancel"), negative: true, callback: this.close},
				{text: this.getString("confirm"), disabled: !this.hasRequiredFields, callback: this.confirm},
			];
		},
	},
	methods: {
		open() {
			let source = this.addressData || {};
			this.fields = {...source};
			this.edited = false;
			this.$refs["popup"].toggleStatus(true);
		},
		close() {
			this.$refs["popup"].toggleStatus(false);
		},
		onChangeField(key, value) {
			this.fields[key] = value || null;
			if(!this.edited && this.addressData?.[key] !== this.fields[key]) {
				this.edited = true;
			}
		},
		confirm() {
			if(this.edited) {
				let merged = Object.assign({}, this.addressData);
				this.editableFields.forEach(field => {
					merged[field.key] = this.fields[field.key] ? this.fields[field.key].trim() : null;
				});
				merged.manual_edit = true;
				this.$emit("confirm-fields", {
					data: merged,
					address: this.composeAddressString(merged),
				});
			}
			this.close();
		},
		composeAddressString(data) {
			// Build the human-readable address "phrase" from the individual fields
			let sameText = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

			// Street: "Via Roma 12"
			// filter(Boolean) strips out falsey values
			let street = [data.via, data.civico].filter(Boolean).join(" ");

			// Locality names (comune, then provincia when it differs), comma-separated
			let localityParts = [];
			if(data.comune) {
				localityParts.push(data.comune);
			}
			if(data.provincia && !sameText(data.provincia, data.comune)) {
				localityParts.push(data.provincia);
			}
			// Attach the province code to the last locality name, e.g. "Roma (RM)"
			if(localityParts.length && data.provincia_codice) {
				let lastLocality = localityParts[localityParts.length - 1];
				if(!sameText(data.provincia_codice, lastLocality)) {
					localityParts[localityParts.length - 1] = lastLocality + " (" + data.provincia_codice + ")";
				}
			}
			// City block: "00040 Rocca di Papa, Roma (RM)" — zip prefixes the locality
			let cityBlock = [data.zip, localityParts.join(", ")].filter(Boolean).join(" ");

			// Region: only when it differs from both comune and provincia
			let region = null;
			if(data.regione && !sameText(data.regione, data.provincia) && !sameText(data.regione, data.comune)) {
				region = data.regione;
			}

			return [street, cityBlock, region, data.country].filter(Boolean).join(", ");
		},
	},
};
</script>

<style lang="scss" scoped>
.edit-address-fields-list {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 14px;

	&:deep(.input-wrapper) {
		width: 100%;
	}
}
</style>